import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Collapse,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
  alpha,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import ShippingInfoSection from './delivery-information-components/ShippingInfoSection';
import OriginDestinationSection from './delivery-information-components/OriginDestinationSection';
import CargoDetailsSection from './delivery-information-components/CargoDetailsSection';
import DocumentationSection from './delivery-information-components/DocumentationSection';
import TermsConditionsSection from './delivery-information-components/TermsConditionsSection';
import BOLPrintable from './delivery-information-components/BOLPrintable';
import { generateBOLNumber, getTodayString } from './delivery-information-components/utils';

const ACCORDION_KEY = 'delivery-information-accordion';

const getInitialAccordionState = () => {
  try {
    const stored = localStorage.getItem(ACCORDION_KEY);
    if (stored) return JSON.parse(stored);
    return { shipping: true, originDest: true, cargo: true, documentation: true, terms: true };
  } catch {
    return { shipping: true, originDest: true, cargo: true, documentation: true, terms: true };
  }
};

const CollapsibleSection = ({ title, expanded, onToggle, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.25, px: 2.5, cursor: 'pointer', userSelect: 'none',
          borderRadius: expanded ? '12px 12px 0 0' : '12px',
          background: 'white',
          border: `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
          borderBottom: expanded ? `1px solid ${alpha(theme.palette.primary.main, 0.12)}` : `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
          boxShadow: expanded ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}` : `0 1px 4px ${alpha(theme.palette.grey[400], 0.15)}`,
          transition: 'box-shadow 0.2s, border-radius 0.2s',
          '&:hover': { boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}`, borderColor: alpha(theme.palette.primary.main, 0.3) }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: expanded ? 'primary.main' : alpha(theme.palette.grey[400], 0.8), transition: 'background-color 0.2s', flexShrink: 0 }} />
          <Typography fontWeight={500} sx={{ fontSize: '0.9rem', color: expanded ? 'primary.main' : 'text.secondary', transition: 'color 0.2s' }}>
            {title}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: expanded ? 'primary.main' : 'text.disabled', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s, color 0.2s' }} />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ '& > *:first-of-type': { mt: '0 !important', borderRadius: '0 0 12px 12px !important', borderTop: 'none !important', animation: 'none !important' } }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const DeliveryInformation = ({ purchaseOrder: propPurchaseOrder, onUpdate: propOnUpdate, currentCompany }) => {
  const { companyId, poId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const printComponentRef = useRef(null);
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('sales.purchase_orders.delivery_info.view', 'read');
  const canEdit = hasPermission('sales.purchase_orders.delivery_info.edit', 'write');
  const canDelete = hasPermission('sales.purchase_orders.delivery_info.delete', 'delete');

  // State for BOL data
  const [bol, setBol] = useState({
    bol_number: '',
    bol_date: getTodayString(),
    // Shipping Info
    carrier_name: '',
    tracking_number: '',
    ship_date: '',
    expected_delivery_date: '',
    delivery_status: 'pending',
    // Origin/Shipper
    shipper_name: '',
    shipper_contact: '',
    shipper_email: '',
    origin_street: '',
    origin_city: '',
    origin_state: '',
    origin_zip: '',
    origin_country: '',
    pickup_location: '',
    // Destination/Consignee
    consignee_name: '',
    consignee_company: '',
    destination_street: '',
    destination_city: '',
    destination_state: '',
    destination_zip: '',
    destination_country: '',
    delivery_location: '',
    // Cargo Details
    goods_description: '',
    container_number: '',
    seal_number: '',
    num_pallets: '',
    num_cases: '',
    num_units: '',
    container_length: '',
    container_width: '',
    container_height: '',
    dimension_unit: 'ft',
    total_weight: '',
    weight_unit: 'lbs',
    hazardous_materials: false,
    hazmat_details: '',
    un_number: '',
    hazard_class: '',
    special_handling: '',
    // Documentation
    freight_terms: 'Prepaid',
    payment_responsibility: 'Consignee',
    freight_charges: '',
    cod_amount: '',
    insurance_required: false,
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_coverage: '',
    insurance_contact: '',
    insurance_details: '',
    documentation_notes: '',
    reference_1: '',
    reference_2: '',
    // Terms
    terms_conditions: '',
    shipper_signature_name: '',
    shipper_signature_title: '',
    shipper_signature_date: getTodayString()
  });

  const [purchaseOrder, setPurchaseOrder] = useState(propPurchaseOrder || null);
  const [lineItems, setLineItems] = useState([]);
  const [company, setCompany] = useState(currentCompany || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printOption, setPrintOption] = useState('summary'); // 'summary', 'lotBreakdown', or 'both'
  const [accordion, setAccordion] = useState(getInitialAccordionState);

  const toggleSection = (key) => {
    setAccordion(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(ACCORDION_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchPurchaseOrder(),
          fetchCompany(),
          fetchBOL()
        ]);
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data: ' + err.message);
        setLoading(false);
      }
    };
    loadData();
  }, [poId, companyId]);

  // Watch for changes in PI number and update BOL number to match
  useEffect(() => {
    const updateBOLNumberFromPI = () => {
      const piNumber = propPurchaseOrder?.po_number || propPurchaseOrder?.customer_po_number || purchaseOrder?.po_number;
      if (piNumber && bol.bol_number) {
        // Extract the number part from PI (e.g., PI-2025-0002 -> BOL-2025-0002)
        const piMatch = piNumber.match(/PI-(\d{4})-(\d+)/);
        if (piMatch) {
          const year = piMatch[1];
          const number = piMatch[2];
          const newBolNumber = `BOL-${year}-${number}`;
          if (bol.bol_number !== newBolNumber) {
            setBol(prev => ({ ...prev, bol_number: newBolNumber }));
          }
        }
      }
    };

    updateBOLNumberFromPI();
  }, [propPurchaseOrder?.po_number, propPurchaseOrder?.customer_po_number, purchaseOrder?.po_number]);

  // Fetch proforma invoice and line items
  const fetchPurchaseOrder = async () => {
    if (propPurchaseOrder) {
      setPurchaseOrder(propPurchaseOrder);
      await fetchLineItems(propPurchaseOrder.id);
      await fetchPOCreationData(propPurchaseOrder.id);
      return;
    }

    if (!poId) return;

    try {
      // Fetch proforma invoice from customer_purchase_orders table
      const { data: customerPO, error: poErr } = await supabase
        .from('customer_purchase_orders')
        .select('*')
        .eq('id', poId)
        .single();

      if (poErr) {
        console.error('Error fetching proforma invoice:', poErr);
        return;
      }

      if (customerPO) {
        setPurchaseOrder(customerPO);
        await fetchLineItems(customerPO.id);
        await fetchPOCreationData(customerPO.id);
      }
    } catch (err) {
      console.error('Error fetching proforma invoice:', err);
    }
  };

  // Fetch PI creation data which has the billing and shipping addresses
  const fetchPOCreationData = async (purchaseOrderId) => {
    try {
      // Get the LATEST po_creation record for this purchase order
      const { data, error } = await supabase
        .from('po_creation')
        .select('*')
        .eq('purchase_order_id', purchaseOrderId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching PI creation data:', error);
        return;
      }

      if (data) {
        // Merge the po_creation data with the proforma invoice
        setPurchaseOrder(prev => ({
          ...prev,
          ...data
        }));
      }
    } catch (err) {
      console.error('Error fetching PI creation data:', err);
    }
  };

  // Fetch line items from po_creation_items table
  const fetchLineItems = async (purchaseOrderId) => {
    try {
      // First, get the LATEST po_creation record for this purchase order
      const { data: poCreationData, error: poCreationError } = await supabase
        .from('po_creation')
        .select('id')
        .eq('purchase_order_id', purchaseOrderId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (poCreationError) {
        console.error('Error fetching po_creation:', poCreationError);
        return;
      }

      if (!poCreationData) {
        setLineItems([]);
        return;
      }

      // Now fetch line items ONLY from this specific po_creation record
      const { data, error } = await supabase
        .from('po_creation_items')
        .select('*')
        .eq('po_creation_id', poCreationData.id)
        .order('line_order');

      if (error) {
        console.error('Error fetching line items:', error);
        return;
      }

      setLineItems(data || []);
    } catch (err) {
      console.error('Error fetching line items:', err);
    }
  };

  // Fetch company info
  const fetchCompany = async () => {
    if (currentCompany) {
      setCompany(currentCompany);
      return;
    }

    if (!companyId) return;

    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(data);
    } catch (err) {
      console.error('Error fetching company:', err);
    }
  };

  // Fetch existing BOL or generate new BOL number based on PI number
  const fetchBOL = async () => {
    const poIdToUse = propPurchaseOrder?.id || poId;
    
    if (!poIdToUse || !companyId) {
      return;
    }

    try {
      // Get the latest PI number from customer_purchase_orders
      const { data: latestPO, error: poErr } = await supabase
        .from('customer_purchase_orders')
        .select('po_number, customer_po_number')
        .eq('id', poIdToUse)
        .maybeSingle();

      if (poErr) {
        console.error('Error fetching PO:', poErr);
      }

      const piNumber = latestPO?.po_number || latestPO?.customer_po_number || propPurchaseOrder?.po_number || propPurchaseOrder?.customer_po_number;

      // Check if BOL already exists for this proforma invoice
      const { data: existingBOL, error } = await supabase
        .from('po_delivery_information')
        .select('*')
        .eq('purchase_order_id', poIdToUse)
        .eq('company_id', companyId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching BOL:', error);
        return;
      }

      if (existingBOL) {
        // Load existing BOL data, but update BOL number to match PI number if it changed
        let bolNumber = existingBOL.bol_number;
        if (piNumber) {
          // Extract the number part from PI (e.g., PI-2025-0002 -> 2025-0002)
          const piMatch = piNumber.match(/PI-(\d{4})-(\d+)/);
          if (piMatch) {
            const year = piMatch[1];
            const number = piMatch[2];
            bolNumber = `BOL-${year}-${number}`;
          }
        }

        setBol({
          ...existingBOL,
          bol_number: bolNumber,
          bol_date: existingBOL.created_at ? new Date(existingBOL.created_at).toISOString().split('T')[0] : getTodayString(),
          dimension_unit: existingBOL.dimension_unit || 'ft',
          payment_responsibility: existingBOL.payment_responsibility || 'Consignee'
        });
      } else {
        // Generate new BOL number based on PI number
        let bolNumber = '';
        if (piNumber) {
          // Extract the number part from PI (e.g., PI-2025-0002 -> BOL-2025-0002)
          const piMatch = piNumber.match(/PI-(\d{4})-(\d+)/);
          if (piMatch) {
            const year = piMatch[1];
            const number = piMatch[2];
            bolNumber = `BOL-${year}-${number}`;
          } else {
            // Fallback: try to extract any numbers
            const fallbackMatch = piNumber.match(/(\d{4})-?(\d+)/);
            if (fallbackMatch) {
              bolNumber = `BOL-${fallbackMatch[1]}-${fallbackMatch[2]}`;
            } else {
              // Last resort: use database function
              const { data: dbBolNumber, error: bolError } = await supabase
                .rpc('generate_bol_number', { p_company_id: companyId });
              if (!bolError && dbBolNumber) {
                bolNumber = dbBolNumber;
              }
            }
          }
        } else {
          // No PI number yet, use database function
          const { data: dbBolNumber, error: bolError } = await supabase
            .rpc('generate_bol_number', { p_company_id: companyId });
          if (!bolError && dbBolNumber) {
            bolNumber = dbBolNumber;
          }
        }

        setBol(prev => ({ ...prev, bol_number: bolNumber }));
      }
    } catch (err) {
      console.error('Error fetching BOL:', err);
    }
  };

  // Update BOL data
  const handleBOLUpdate = (updates) => {
    setBol(prev => ({ ...prev, ...updates }));
  };

  // Save BOL
  const handleSave = async () => {
    const poIdToUse = propPurchaseOrder?.id || purchaseOrder?.id || poId;
    
    if (!poIdToUse || !companyId) {
      setError('Proforma Invoice information is missing');
      return;
    }

    // Get the latest PI number and ensure BOL number matches
    const { data: latestPO, error: poErr } = await supabase
      .from('customer_purchase_orders')
      .select('po_number, customer_po_number')
      .eq('id', poIdToUse)
      .maybeSingle();

    if (poErr) {
      console.error('Error fetching PO:', poErr);
    }

    const piNumber = latestPO?.po_number || latestPO?.customer_po_number || propPurchaseOrder?.po_number || propPurchaseOrder?.customer_po_number || purchaseOrder?.po_number;
    
    // Generate BOL number from PI number
    let bolNumber = bol.bol_number;
    if (piNumber) {
      const piMatch = piNumber.match(/PI-(\d{4})-(\d+)/);
      if (piMatch) {
        const year = piMatch[1];
        const number = piMatch[2];
        bolNumber = `BOL-${year}-${number}`;
      }
    }

    if (!bolNumber) {
      setError('BOL number is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Update local state with correct BOL number
      setBol(prev => ({ ...prev, bol_number: bolNumber }));
      
      // Prepare BOL data for database
      const bolData = {
        company_id: companyId,
        purchase_order_id: poIdToUse,
        bol_number: bolNumber,
        
        // Shipping Information
        carrier_name: bol.carrier_name || null,
        tracking_number: bol.tracking_number || null,
        ship_date: bol.ship_date || null,
        expected_delivery_date: bol.expected_delivery_date || null,
        delivery_status: bol.delivery_status || 'pending',
        
        // Origin (Shipper)
        shipper_name: bol.shipper_name || null,
        shipper_contact: bol.shipper_contact || null,
        shipper_email: bol.shipper_email || null,
        origin_street: bol.origin_street || null,
        origin_city: bol.origin_city || null,
        origin_state: bol.origin_state || null,
        origin_zip: bol.origin_zip || null,
        origin_country: bol.origin_country || null,
        pickup_location: bol.pickup_location || null,
        
        // Destination (Consignee)
        consignee_name: bol.consignee_name || null,
        consignee_company: bol.consignee_company || null,
        destination_street: bol.destination_street || null,
        destination_city: bol.destination_city || null,
        destination_state: bol.destination_state || null,
        destination_zip: bol.destination_zip || null,
        destination_country: bol.destination_country || null,
        delivery_location: bol.delivery_location || null,
        
        // Cargo
        goods_description: bol.goods_description || null,
        container_number: bol.container_number || null,
        seal_number: bol.seal_number || null,
        num_pallets: bol.num_pallets ? parseInt(bol.num_pallets) : 0,
        num_cases: bol.num_cases ? parseInt(bol.num_cases) : 0,
        num_units: bol.num_units ? parseInt(bol.num_units) : 0,
        container_length: bol.container_length ? parseFloat(bol.container_length) : null,
        container_width: bol.container_width ? parseFloat(bol.container_width) : null,
        container_height: bol.container_height ? parseFloat(bol.container_height) : null,
        dimension_unit: bol.dimension_unit || 'ft',
        total_weight: bol.total_weight ? parseFloat(bol.total_weight) : null,
        weight_unit: bol.weight_unit || 'lbs',
        hazardous_materials: bol.hazardous_materials || false,
        hazmat_details: bol.hazmat_details || null,
        un_number: bol.un_number || null,
        hazard_class: bol.hazard_class || null,
        special_handling: bol.special_handling || null,
        
        // Documentation
        freight_terms: bol.freight_terms || null,
        payment_responsibility: bol.payment_responsibility || null,
        freight_charges: bol.freight_charges ? parseFloat(bol.freight_charges) : null,
        cod_amount: bol.cod_amount ? parseFloat(bol.cod_amount) : null,
        insurance_required: bol.insurance_required || false,
        insurance_provider: bol.insurance_provider || null,
        insurance_policy_number: bol.insurance_policy_number || null,
        insurance_coverage: bol.insurance_coverage ? parseFloat(bol.insurance_coverage) : null,
        insurance_contact: bol.insurance_contact || null,
        insurance_details: bol.insurance_details || null,
        documentation_notes: bol.documentation_notes || null,
        reference_1: bol.reference_1 || null,
        reference_2: bol.reference_2 || null,
        
        // Terms & Signatures
        terms_conditions: bol.terms_conditions || null,
        shipper_signature_name: bol.shipper_signature_name || null,
        shipper_signature_title: bol.shipper_signature_title || null,
        shipper_signature_date: bol.shipper_signature_date || null,
        
        // Metadata
        created_by: user?.id || null,
        stage_completed: true,
        completed_at: new Date().toISOString(),
        completed_by: user?.id || null
      };

      // Check if BOL already exists
      const { data: existingBOL, error: checkError } = await supabase
        .from('po_delivery_information')
        .select('id')
        .eq('purchase_order_id', poIdToUse)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let result;
      if (existingBOL) {
        // Update existing BOL
        const { data, error } = await supabase
          .from('po_delivery_information')
          .update(bolData)
          .eq('id', existingBOL.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        // Insert new BOL
        const { data, error } = await supabase
          .from('po_delivery_information')
          .insert([bolData])
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      // Update stage_completed in customer_purchase_orders table
      // First fetch current stage_completed
      const { data: currentPO, error: fetchPOError } = await supabase
        .from('customer_purchase_orders')
        .select('stage_completed')
        .eq('id', poIdToUse)
        .single();

      if (fetchPOError) {
        console.error('Error fetching PI stages:', fetchPOError);
      } else {
        // Add 'delivery_info' to stage_completed if not already present
        const stageCompleted = currentPO?.stage_completed || [];
        if (!stageCompleted.includes('delivery_info')) {
          stageCompleted.push('delivery_info');
        }

        const { error: updatePOError } = await supabase
          .from('customer_purchase_orders')
          .update({
            stage_completed: stageCompleted,
            current_stage: 'review'
          })
          .eq('id', poIdToUse);

        if (updatePOError) {
          console.error('Error updating PI stage:', updatePOError);
        }
      }

      setSuccessMessage('Bill of Lading saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Call parent update if in workflow mode
      if (propOnUpdate) {
        propOnUpdate(poIdToUse);
      }
      
      setSaving(false);
    } catch (err) {
      console.error('Error saving BOL:', err);
      setError('Failed to save Bill of Lading: ' + err.message);
      setSaving(false);
    }
  };

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Print BOL
  const handlePrint = () => {
    document.title = `BOL-${bol.bol_number}`;
    
    try {
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      // Convert country code to full name
      const getCountryFullName = (country) => {
        if (!country) return '';
        const countryMap = {
          'US': 'United States',
          'USA': 'United States',
          'United States': 'United States',
          'CA': 'Canada',
          'Canada': 'Canada',
          'MX': 'Mexico',
          'Mexico': 'Mexico',
          'UK': 'United Kingdom',
          'GB': 'United Kingdom',
          'United Kingdom': 'United Kingdom'
        };
        return countryMap[country] || country;
      };

      const formatAddressFromFields = (street, city, state, zip, country) => {
        const addressParts = [];
        if (street) addressParts.push(street);
        
        const cityStateZip = [];
        if (city) cityStateZip.push(city);
        if (state) cityStateZip.push(state);
        if (zip) cityStateZip.push(zip);
        
        if (cityStateZip.length > 0) {
          addressParts.push(cityStateZip.join(', '));
        }
        
        if (country) addressParts.push(getCountryFullName(country));
        
        return addressParts.join('<br/>');
      };

      const formatCompanyAddr = () => {
        if (!company) return '';
        
        let address = '';
        if (company.address) address += company.address;
        
        if (company.city || company.state || company.zip_code) {
          address += address ? '<br/>' : '';
          if (company.city) address += company.city;
          if (company.state) {
            if (company.city) address += ', ';
            address += company.state;
          }
          if (company.zip_code) {
            if (company.city || company.state) address += ' ';
            address += company.zip_code;
          }
        }
        
        if (company.country) {
          address += address ? '<br/>' : '';
          address += getCountryFullName(company.country);
        }
        
        return address;
      };

      const printableContent = `
        <div style="padding: 24px; backgroundColor: #fff; font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">
            <div>
              <h1 style="margin: 0 0 6px 0; font-size: 1.75rem; font-weight: 400;">BILL OF LADING</h1>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                BOL #: ${bol.bol_number}
              </p>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Proforma Invoice #: ${purchaseOrder?.po_number || 'N/A'}
              </p>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Date: ${formatDate(new Date().toISOString().split('T')[0])}
              </p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin: 0 0 6px 0; font-size: 1.1rem; font-weight: 400;">
                ${company?.name || 'Your Company Name'}
              </h2>
              <p style="margin: 1px 0; font-size: 0.8rem;">
                ${formatCompanyAddr() || 'Address not provided'}
              </p>
              ${company?.phone ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Phone: ${company.phone}
              </p>` : ''}
              ${company?.email ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Email: ${company.email}
              </p>` : ''}
            </div>
          </div>

          ${(bol.carrier_name || bol.tracking_number) ? `
          <div style="margin-bottom: 20px; padding: 16px; background-color: #f5f5f5; border-radius: 4px;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Shipping Information</h3>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
              ${bol.carrier_name ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Carrier:</strong> ${bol.carrier_name}</p>` : ''}
              ${bol.tracking_number ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Tracking #:</strong> ${bol.tracking_number}</p>` : ''}
              ${bol.ship_date ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Ship Date:</strong> ${formatDate(bol.ship_date)}</p>` : ''}
              ${bol.expected_delivery_date ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Expected Delivery:</strong> ${formatDate(bol.expected_delivery_date)}</p>` : ''}
              ${bol.delivery_status ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Status:</strong> ${bol.delivery_status.replace(/_/g, ' ').toUpperCase()}</p>` : ''}
            </div>
          </div>` : ''}

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
            <div>
              <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem; border-bottom: 1px solid #000;">Shipper (From):</h3>
              <p style="margin: 2px 0; font-size: 0.8rem;">${bol.shipper_name || 'Not specified'}</p>
              ${bol.shipper_contact ? `<p style="margin: 2px 0; font-size: 0.8rem;">Phone: ${bol.shipper_contact}</p>` : ''}
              ${bol.shipper_email ? `<p style="margin: 2px 0; font-size: 0.8rem;">Email: ${bol.shipper_email}</p>` : ''}
              <p style="margin: 6px 0; font-size: 0.8rem;">
                ${formatAddressFromFields(bol.origin_street, bol.origin_city, bol.origin_state, bol.origin_zip, bol.origin_country) || 'No address provided'}
              </p>
              ${bol.pickup_location ? `<p style="margin: 2px 0; font-size: 0.8rem; font-style: italic;">Pickup: ${bol.pickup_location}</p>` : ''}
            </div>
            <div>
              <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem; border-bottom: 1px solid #000;">Consignee (To):</h3>
              <p style="margin: 2px 0; font-size: 0.8rem;">${bol.consignee_name || 'Not specified'}</p>
              ${bol.consignee_company ? `<p style="margin: 2px 0; font-size: 0.8rem;">Company: ${bol.consignee_company}</p>` : ''}
              <p style="margin: 6px 0; font-size: 0.8rem;">
                ${formatAddressFromFields(bol.destination_street, bol.destination_city, bol.destination_state, bol.destination_zip, bol.destination_country) || 'No address provided'}
              </p>
              ${bol.delivery_location ? `<p style="margin: 2px 0; font-size: 0.8rem; font-style: italic;">Delivery: ${bol.delivery_location}</p>` : ''}
            </div>
          </div>

          <div style="margin-bottom: 20px;">
            <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem; border-bottom: 1px solid #000;">Cargo Description</h3>
            <p style="margin: 6px 0; font-size: 0.8rem; white-space: pre-line;">
              ${bol.goods_description || 'See line items below'}
            </p>
            ${(bol.container_number || bol.seal_number) ? `
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 6px 0;">
              ${bol.container_number ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Container #:</strong> ${bol.container_number}</p>` : ''}
              ${bol.seal_number ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Seal #:</strong> ${bol.seal_number}</p>` : ''}
            </div>
            ` : ''}
            ${(bol.num_pallets || bol.num_cases || bol.num_units) ? `
            <p style="margin: 6px 0; font-size: 0.8rem;">
              <strong>Quantity:</strong> 
              ${bol.num_pallets ? `${bol.num_pallets} Pallet(s) ` : ''}
              ${bol.num_cases ? `${bol.num_cases} Case(s) ` : ''}
              ${bol.num_units ? `${bol.num_units} Unit(s)` : ''}
            </p>` : ''}
            ${(bol.container_length || bol.container_width || bol.container_height) ? `
            <p style="margin: 6px 0; font-size: 0.8rem;">
              <strong>Dimensions:</strong> 
              ${bol.container_length || ''}${bol.container_width ? ` x ${bol.container_width}` : ''}${bol.container_height ? ` x ${bol.container_height}` : ''} ${bol.dimension_unit || ''}
            </p>` : ''}
            ${bol.total_weight ? `<p style="margin: 6px 0; font-size: 0.8rem;"><strong>Weight:</strong> ${bol.total_weight} ${bol.weight_unit || 'lbs'}</p>` : ''}
            ${bol.hazardous_materials ? `
            <div style="border: 1px solid #d32f2f; border-radius: 4px; padding: 12px; margin-top: 8px; background-color: #ffebee;">
              <p style="margin: 0 0 8px 0; font-size: 0.8rem; color: #d32f2f; font-weight: bold;">HAZARDOUS MATERIALS</p>
              ${bol.hazmat_details ? `<p style="margin: 0 0 4px 0; font-size: 0.8rem;">${bol.hazmat_details}</p>` : ''}
              ${bol.un_number ? `<p style="margin: 0 0 4px 0; font-size: 0.8rem;"><strong>UN Number:</strong> ${bol.un_number}</p>` : ''}
              ${bol.hazard_class ? `<p style="margin: 0; font-size: 0.8rem;"><strong>Hazard Class:</strong> ${bol.hazard_class}</p>` : ''}
            </div>
            ` : ''}
            ${bol.special_handling ? `<p style="margin: 6px 0; font-size: 0.8rem;"><strong>Special Handling:</strong> ${bol.special_handling}</p>` : ''}
          </div>

          ${(lineItems && lineItems.length > 0 && (printOption === 'summary' || printOption === 'both')) ? `
          <!-- Order Summary Table -->
          <div style="margin: 24px 0 12px 0;">
            <h3 style="margin: 0 0 8px 0; padding-bottom: 8px; font-weight: 600; font-size: 1.1rem; border-bottom: 1px solid rgba(0, 0, 0, 0.12);">Order Summary</h3>
          </div>
          <div style="border: 1px solid rgba(224, 224, 224, 1); border-radius: 4px; margin: 16px 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: rgb(245, 245, 245);">
                  <th style="padding: 10px; text-align: left; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Item</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Size</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">UoM</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Qty</th>
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  // Group items by product_id and variant_id, sum quantities
                  const summaryMap = new Map();
                  
                  lineItems.filter(item => item.description).forEach(item => {
                    const key = `${item.product_id || 'no-product'}_${item.variant_id || 'no-variant'}_${item.unit_type || 'Case'}`;
                    
                    if (!summaryMap.has(key)) {
                      summaryMap.set(key, {
                        product_name: item.product_name || item.description?.split(' - ')[0] || 'N/A',
                        variant_name: item.variant_name || null,
                        unit_type: item.unit_type || 'Case',
                        units_per_case: item.units_per_case || null,
                        quantity: 0
                      });
                    }

                    const summary = summaryMap.get(key);
                    summary.quantity += parseFloat(item.quantity) || 0;
                  });

                  return Array.from(summaryMap.values()).map((summary, idx) => {
                    const isCase = (summary.unit_type || 'Case') === 'Case';
                    const qtyLabel = isCase
                      ? `${summary.quantity} ${summary.quantity === 1 ? 'Case' : 'Cases'}`
                      : `${summary.quantity}`;
                    return `
                    <tr>
                      <td style="padding: 8px 10px; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.product_name}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.variant_name || '-'}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.units_per_case ? `${summary.units_per_case}U/C` : '-'}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${qtyLabel}</td>
                    </tr>
                    `;
                  }).join('');
                })()}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${(lineItems && lineItems.length > 0 && (printOption === 'lotBreakdown' || printOption === 'both')) ? `
          <!-- Lot Breakdown Table -->
          <div style="margin: 24px 0 12px 0;">
            <h3 style="margin: 0 0 8px 0; padding-bottom: 8px; font-weight: 600; font-size: 1.1rem; border-bottom: 1px solid rgba(0, 0, 0, 0.12);">Lot Breakdown</h3>
          </div>
          <div style="border: 1px solid rgba(224, 224, 224, 1); border-radius: 4px; margin: 16px 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: rgb(245, 245, 245);">
                  <th style="padding: 10px; text-align: left; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Item</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Size</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">UoM</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Qty</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Lot #</th>
                </tr>
              </thead>
              <tbody>
                ${lineItems.filter(item => item.description).map(item => {
                  const isCase = (item.unit_type || 'Case') === 'Case';
                  const qty = parseFloat(item.quantity) || 0;
                  const qtyLabel = isCase ? `${qty} ${qty === 1 ? 'Case' : 'Cases'}` : `${qty}`;
                  return `
                <tr>
                  <td style="padding: 8px 10px; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.product_name || item.description.split(' - ')[0] || 'N/A'}</td>
                  <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.variant_name || '-'}</td>
                  <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.units_per_case ? `${item.units_per_case}U/C` : '-'}</td>
                  <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${qtyLabel}</td>
                  <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.lot_number || '-'}</td>
                </tr>
                `;
                }).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${(bol.freight_terms || bol.insurance_required) ? `
          <div style="margin-bottom: 20px; padding: 16px; background-color: #f5f5f5; border-radius: 4px;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Payment & Documentation</h3>
            ${bol.freight_terms ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Freight Terms:</strong> ${bol.freight_terms}</p>` : ''}
            ${bol.payment_responsibility ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Payment By:</strong> ${bol.payment_responsibility}</p>` : ''}
            ${bol.freight_charges ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>Freight Charges:</strong> ${formatCurrency(bol.freight_charges)}</p>` : ''}
            ${bol.cod_amount ? `<p style="margin: 2px 0; font-size: 0.8rem;"><strong>COD Amount:</strong> ${formatCurrency(bol.cod_amount)}</p>` : ''}
            ${bol.insurance_required ? `
            <p style="margin: 10px 0 5px 0; font-size: 0.8rem; font-weight: 600;">Insurance:</p>
            ${bol.insurance_provider ? `<p style="margin: 2px 0; font-size: 0.8rem;">Provider: ${bol.insurance_provider}</p>` : ''}
            ${bol.insurance_policy_number ? `<p style="margin: 2px 0; font-size: 0.8rem;">Policy: ${bol.insurance_policy_number}</p>` : ''}
            ${bol.insurance_coverage ? `<p style="margin: 2px 0; font-size: 0.8rem;">Coverage: ${formatCurrency(bol.insurance_coverage)}</p>` : ''}
            ${bol.insurance_contact ? `<p style="margin: 2px 0; font-size: 0.8rem;">Contact: ${bol.insurance_contact}</p>` : ''}
            ${bol.insurance_details ? `<p style="margin: 2px 0; font-size: 0.8rem;">Details: ${bol.insurance_details}</p>` : ''}
            ` : ''}
            ${(bol.reference_1 || bol.reference_2) ? `
            <p style="margin: 10px 0 5px 0; font-size: 0.8rem; font-weight: 600;">Reference Numbers:</p>
            ${bol.reference_1 ? `<p style="margin: 2px 0; font-size: 0.8rem;">Ref #1: ${bol.reference_1}</p>` : ''}
            ${bol.reference_2 ? `<p style="margin: 2px 0; font-size: 0.8rem;">Ref #2: ${bol.reference_2}</p>` : ''}
            ` : ''}
            ${bol.documentation_notes ? `
            <p style="margin: 10px 0 5px 0; font-size: 0.8rem; font-weight: 600;">Additional Notes:</p>
            <p style="margin: 2px 0; font-size: 0.8rem; white-space: pre-line;">${bol.documentation_notes}</p>
            ` : ''}
          </div>` : ''}

          ${bol.terms_conditions ? `
          <div class="terms-box" style="margin-bottom: 20px; padding: 16px; border: 1px solid #000;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Terms and Conditions</h3>
            <p style="margin: 0; font-size: 0.75rem; white-space: pre-line;">${bol.terms_conditions}</p>
          </div>` : ''}

          <div style="margin-top: 30px; page-break-inside: avoid;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <div style="width: 45%; border-top: 1px solid #000; padding-top: 5px;">
                <p style="margin: 2px 0; font-size: 0.8rem; font-weight: 600;">Shipper Signature</p>
              ${bol.shipper_signature_name ? `
              <div style="font-family: 'Brush Script MT', cursive; font-size: 18pt; margin: 6px 0;">${bol.shipper_signature_name}</div>
              ${bol.shipper_signature_title ? `<p style="margin: 1px 0; font-size: 0.75rem;">Title: ${bol.shipper_signature_title}</p>` : ''}
              ${bol.shipper_signature_date ? `<p style="margin: 1px 0; font-size: 0.75rem;">Date: ${formatDate(bol.shipper_signature_date)}</p>` : ''}
              ` : `
              <div style="height: 40px;"></div>
              <p style="margin: 1px 0; font-size: 0.75rem;">Title: <span style="display: inline-block; width: 200px; border-bottom: 1px solid #000;"></span></p>
              <p style="margin: 1px 0; font-size: 0.75rem;">Date: <span style="display: inline-block; width: 200px; border-bottom: 1px solid #000;"></span></p>
              `}
              </div>
              <div style="width: 45%; border-top: 1px solid #000; padding-top: 5px;">
                <p style="margin: 2px 0; font-size: 0.8rem; font-weight: 600;">Carrier/Driver Signature</p>
                <div style="height: 40px;"></div>
                <p style="margin: 1px 0 8px 0; font-size: 0.75rem;">Driver Name: <span style="display: inline-block; width: 180px; border-bottom: 1px solid #000;"></span></p>
                <p style="margin: 1px 0; font-size: 0.75rem;">Date: <span style="display: inline-block; width: 200px; border-bottom: 1px solid #000;"></span></p>
              </div>
            </div>

            <div style="border-top: 1px solid #000; padding-top: 5px;">
              <div style="width: 45%;">
                <p style="margin: 2px 0; font-size: 0.8rem; font-weight: 600;">Consignee Signature (Delivery Receipt)</p>
                <div style="height: 40px;"></div>
                <p style="margin: 1px 0 8px 0; font-size: 0.75rem;">Received By: <span style="display: inline-block; width: 180px; border-bottom: 1px solid #000;"></span></p>
                <p style="margin: 1px 0 8px 0; font-size: 0.75rem;">Date: <span style="display: inline-block; width: 200px; border-bottom: 1px solid #000;"></span></p>
                <p style="margin: 1px 0; font-size: 0.75rem;">Condition of Goods: <span style="display: inline-block; width: 150px; border-bottom: 1px solid #000;"></span></p>
              </div>
            </div>
          </div>
        </div>
      `;

      // Create new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Bill of Lading ${bol.bol_number}</title>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap" rel="stylesheet">
          <style>
            @page { 
              size: auto; 
              margin: 0.3in; 
            }
            @media print {
              body { 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .terms-box {
                box-decoration-break: clone;
                -webkit-box-decoration-break: clone;
              }
            }
            body {
              margin: 0;
              font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
              line-height: 1.3;
              letter-spacing: 0.01071em;
              background: white;
            }
            .terms-box {
              box-decoration-break: clone;
              -webkit-box-decoration-break: clone;
            }
          </style>
        </head>
        <body>
          ${printableContent}
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        
        setTimeout(() => {
          printWindow.close();
          setPrintDialogOpen(false);
        }, 1000);
      };
      
    } catch (error) {
      console.error('Print error:', error);
      setError('Error printing Bill of Lading. Please try again.');
    }
  };

  // Access Denied Card
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Delivery Information Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Delivery Information. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <ShippingIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Delivery Information
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Retrieving your delivery data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (

    <Fade in timeout={800}>
      <Box>
        <Container maxWidth={false} sx={{ py: 3, pb: 8, px: 0, marginLeft: '120px' }}>
          {/* Page Header */}
          <Card sx={{ 
            borderRadius: 3,
            mb: 3,
            mt: 3,
            width: 'auto',
            border: `1px solid ${theme.palette.grey[400]}`,
            background: theme.palette.grey[100],
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 3, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}>
                    <ShippingIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main',
                      fontSize: '2rem'
                    }}>
                      Bill of Lading - {bol.bol_number}
      </Typography>

                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      {purchaseOrder?.po_number ? `Proforma Invoice #${purchaseOrder.po_number}` : 'New BOL'}
      </Typography>
    </Box>

                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<PrintIcon />} 
                    onClick={() => setPrintDialogOpen(true)}
                    sx={{ 
                      borderRadius: 3, 
                      px: 3, 
                      py: 1.5,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Preview
                  </Button>
                  {canEdit && (
                    <Button 
                      variant="contained" 
                      startIcon={<SaveIcon />} 
                      onClick={handleSave} 
                      disabled={saving}
                      sx={{ 
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontWeight: 500,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`
                        },
                        '&:disabled': {
                          background: alpha(theme.palette.primary.main, 0.5)
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {saving ? 'Saving...' : 'Save BOL'}
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: 3, 
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                backgroundColor: alpha(theme.palette.success.main, 0.1)
              }}
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </Alert>
          )}

          <CollapsibleSection title="Shipping Info" expanded={accordion.shipping} onToggle={() => toggleSection('shipping')}>
            <ShippingInfoSection bol={bol} onUpdate={handleBOLUpdate} canEdit={canEdit} />
          </CollapsibleSection>

          <CollapsibleSection title="Origin & Destination" expanded={accordion.originDest} onToggle={() => toggleSection('originDest')}>
            <OriginDestinationSection bol={bol} onUpdate={handleBOLUpdate} purchaseOrder={purchaseOrder} company={company} canEdit={canEdit} />
          </CollapsibleSection>

          <CollapsibleSection title="Cargo Details" expanded={accordion.cargo} onToggle={() => toggleSection('cargo')}>
            <CargoDetailsSection bol={bol} onUpdate={handleBOLUpdate} lineItems={lineItems} canEdit={canEdit} />
          </CollapsibleSection>

          <CollapsibleSection title="Documentation" expanded={accordion.documentation} onToggle={() => toggleSection('documentation')}>
            <DocumentationSection bol={bol} onUpdate={handleBOLUpdate} canEdit={canEdit} />
          </CollapsibleSection>

          <CollapsibleSection title="Terms & Conditions" expanded={accordion.terms} onToggle={() => toggleSection('terms')}>
            <TermsConditionsSection bol={bol} onUpdate={handleBOLUpdate} canEdit={canEdit} canDelete={canDelete} />
          </CollapsibleSection>

          {/* Bottom Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => setPrintDialogOpen(true)}
              sx={{ borderRadius: 3, px: 3, py: 1.5 }}
            >
              Preview BOL
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                }}
              >
                {saving ? 'Saving...' : 'Save Bill of Lading'}
              </Button>
            )}
          </Box>

          {/* Print Preview Dialog */}
          <Dialog
            open={printDialogOpen}
            onClose={() => setPrintDialogOpen(false)}
            maxWidth="md"
            fullWidth
            disableScrollLock
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontWeight: 500,
              color: 'primary.main'
            }}>
              <Typography variant="h6">Bill of Lading Preview</Typography>
              <IconButton onClick={() => setPrintDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <BOLPrintable
                ref={printComponentRef}
                bol={bol}
                purchaseOrder={purchaseOrder}
                company={company}
                lineItems={lineItems.filter(item => item.description)}
                showSummary={printOption === 'summary' || printOption === 'both'}
                showLotBreakdown={printOption === 'lotBreakdown' || printOption === 'both'}
              />
            </DialogContent>
            <DialogActions sx={{ p: 3, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>Print Options</FormLabel>
                <RadioGroup
                  row
                  value={printOption}
                  onChange={(e) => setPrintOption(e.target.value)}
                >
                  <FormControlLabel value="summary" control={<Radio />} label="Summary Only" />
                  <FormControlLabel value="lotBreakdown" control={<Radio />} label="Lot Breakdown Only" />
                  <FormControlLabel value="both" control={<Radio />} label="Both" />
                </RadioGroup>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 2 }}>
                <Button onClick={() => setPrintDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
                  Print
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Fade>
  );
};

export default DeliveryInformation;